import React, { createContext, useContext, useId, useState } from 'react';

interface CardCollapseContextValue {
  collapsible: boolean;
  open: boolean;
  toggle: () => void;
  panelId: string;
}

const CardCollapseContext = createContext<CardCollapseContextValue | null>(null);

interface CardProps {
  children: React.ReactNode;
  className?: string;
  id?: string;
  /** Chevron on CardHeader that toggles CardBody. Off unless set — only stacked profile sections should opt in. */
  collapsible?: boolean;
  defaultOpen?: boolean;
}

interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface CardBodyProps {
  children: React.ReactNode;
  className?: string;
}

export const Card: React.FC<CardProps> = ({
  children,
  className = '',
  id,
  collapsible = false,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();

  return (
    <CardCollapseContext.Provider
      value={{
        collapsible,
        open,
        toggle: () => setOpen(value => !value),
        panelId,
      }}
    >
      <div id={id} className={`relative z-0 rounded-lg shadow-sm border border-brand-border overflow-hidden bg-white ${className}`}>
        {children}
      </div>
    </CardCollapseContext.Provider>
  );
};

export const CardHeader: React.FC<CardHeaderProps> = ({ children, className = '' }) => {
  const ctx = useContext(CardCollapseContext);
  const collapsible = ctx?.collapsible ?? false;
  const open = ctx?.open ?? true;
  const collapsed = collapsible && !open;

  const handleHeaderClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!collapsible || !ctx) return;
    const target = event.target as HTMLElement;
    if (target.closest('a, button, input, select, textarea, label')) return;
    ctx.toggle();
  };

  return (
    <div
      className={`px-5 py-3.5 md:px-5 flex items-start gap-3 ${
        collapsed ? '' : 'border-b border-brand-border'
      } ${collapsible ? 'cursor-pointer' : ''} ${className}`}
      onClick={handleHeaderClick}
    >
      <div className="flex-1 min-w-0">{children}</div>
      {collapsible && ctx && (
        <button
          type="button"
          aria-expanded={open}
          aria-controls={ctx.panelId}
          aria-label={open ? 'Collapse section' : 'Expand section'}
          onClick={event => {
            event.stopPropagation();
            ctx.toggle();
          }}
          className="relative z-0 shrink-0 w-8 h-8 min-h-0 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-colors outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
        >
          <svg
            className={`w-5 h-5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}
    </div>
  );
};

export const CardBody: React.FC<CardBodyProps> = ({ children, className = '' }) => {
  const ctx = useContext(CardCollapseContext);
  const collapsible = ctx?.collapsible ?? false;
  const open = ctx?.open ?? true;

  const inner = (
    <div className={`px-5 py-4 md:px-5 ${className}`}>
      {children}
    </div>
  );

  if (!collapsible || !ctx) return inner;

  return (
    <div
      id={ctx.panelId}
      role="region"
      className={`grid transition-[grid-template-rows] duration-200 ease-out ${
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      }`}
    >
      <div className="overflow-hidden min-h-0" aria-hidden={!open}>
        {inner}
      </div>
    </div>
  );
};
